import { getActiveEffect, trackEffect, triggerEffects } from './lifecycle'
import { PackedDepSet } from './packed-dep-set'

/**
 * @deprecated V1 Proxy reactivity — scheduled for removal after V2 stabilization.
 *
 * V2 (getter/setter via `./reactive-v2`) is the default since Phase 1 of the
 * 2026-05-16 performance overhaul. This file is kept callable so that setting
 * `FLEXIUM_REACTIVITY_V2=0` provides an emergency rollback during the soak
 * period; see Plan Phase D Task 26 in
 * `docs/superpowers/plans/2026-05-16-perf-phase1-reactivity-core.md` for the
 * removal timeline.
 *
 * Phase 4: Dep storage was migrated from `Set<ReactiveEffect>` to
 * `PackedDepSet` (Uint32Array of effect IDs) for cache-locality and memory wins.
 */

export const REACTIVE_SIGNAL = Symbol('flexium.reactive')

type Dep = PackedDepSet
type KeyToDepMap = Map<unknown, Dep>
const targetMap = new WeakMap<object, KeyToDepMap>()

// WeakMap to store existing proxies to avoid duplicates
const reactiveMap = new WeakMap<object, object>()

export function reactive<T extends object>(target: T): T {
    if (target && (target as Record<symbol, unknown>)[REACTIVE_SIGNAL]) {
        return target
    }

    const existingProxy = reactiveMap.get(target)
    if (existingProxy) {
        return existingProxy as T
    }

    const proxy = new Proxy(target, {
        get(target, key, receiver) {
            if (key === REACTIVE_SIGNAL) return true

            const res = Reflect.get(target, key, receiver)

            track(target, key)

            if (res !== null && typeof res === 'object') {
                return reactive(res)
            }

            return res
        },
        set(target, key, value, receiver) {
            const oldValue = (target as Record<string | symbol, unknown>)[key]
            const result = Reflect.set(target, key, value, receiver)

            if (result && hasChanged(value, oldValue)) {
                trigger(target, key)
            }

            return result
        }
    })

    reactiveMap.set(target, proxy)
    return proxy
}

export function track(target: object, key: unknown) {
    if (!getActiveEffect()) return

    let depsMap = targetMap.get(target)
    if (!depsMap) {
        targetMap.set(target, (depsMap = new Map()))
    }

    let dep = depsMap.get(key)
    if (!dep) {
        depsMap.set(key, (dep = new PackedDepSet()))
    }

    trackEffect(dep)
}

export function trigger(target: object, key: unknown) {
    const depsMap = targetMap.get(target)
    if (!depsMap) return

    const dep = depsMap.get(key)
    if (dep) {
        triggerEffects(dep)
    }
}

function hasChanged(value: unknown, oldValue: unknown): boolean {
    return !Object.is(value, oldValue)
}

export function isReactive(value: unknown): boolean {
    return !!(value && (value as Record<symbol, unknown>)[REACTIVE_SIGNAL])
}
